import type { MorphemeGlyphTileGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  type MorphemeStagesByGrapheme,
  useMorphemeStagesTableChangeVersion,
  useReadMorphemeStagesPage,
} from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { infiniteQueryOptions, useInfiniteQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { useEffect, useMemo } from "react";
import { createTileSliceCache } from "#create-tile-slice-cache";
import { getTileContentDatabaseId } from "#get-tile-content-database-id";
import {
  buildGraphemeMorphemeSliceByGrapheme,
  graphemePinyinLookupQueryOptions,
} from "#use-morpheme-glyph-tile-grapheme-morpheme-slice";
import type { BatchedGraphemeTileData } from "#use-batched-grapheme-tile-data";
import type { MorphemeGlyphTileStage } from "#morpheme-glyph-tile-ui";

/**
 * Graphemes per tile-data page: six rows of the 8-column summary grid — more than a phone
 * viewport, so one page fills the screen and the next is fetched by the time it scrolls into view.
 * Comfortably under the 64-lookup DataLoader batch the content reads are bounded to.
 */
export const GRAPHEME_TILE_DATA_PAGE_SIZE = 48;

/** Stage a tile renders with before its page has resolved. */
const PLACEHOLDER_STAGE: MorphemeGlyphTileStage = "unadded";

type GraphemeTileDataPage = {
  offset: number;
  sliceByGrapheme: ReadonlyMap<string, MorphemeGlyphTileGraphemeMorphemeSlice>;
  stageByGrapheme: MorphemeStagesByGrapheme;
};

export type PagedGraphemeTileData = BatchedGraphemeTileData & {
  /** How many leading graphemes of the list have their tile data resolved. */
  loadedCount: number;
};

/**
 * The PAGED counterpart of `useBatchedGraphemeTileData`: the two batched tile reads (SRS stage +
 * pinyin slice) for an ordered grapheme list, fetched a page at a time as an infinite query that
 * follows a reveal window — it keeps fetching the next page while fewer than `requiredCount`
 * graphemes have data, one page at a time, and never re-reads a page on the next reveal.
 *
 * This is the summary's answer to the same problem the deck solves with
 * `useDeckCardDetailsInfiniteQuery`: a due queue is hundreds of graphemes, and a single batched read
 * over the whole set (plus the hundreds of tiles it feeds) was the cost that stood between
 * "Welcome Back" and the grid. With paging, the first paint costs one 48-grapheme page.
 *
 * Both resolvers return neutral placeholders for a grapheme whose page is pending, so a tile stays
 * on the prop-resolved branch for its whole lifetime (see `useBatchedGraphemeTileData` for why that
 * matters) and simply re-renders when its page lands.
 *
 * Freshness: the sequence's key is registered through `useMorphemeStagesTableChangeVersion`, so a
 * committed write to any study card invalidates the sequence (TanStack refetches the loaded pages),
 * while each page's overlay reads are registered actor cache entries of their own. The content-DB pinyin reads are immutable and shared with the batched
 * hook's cache.
 *
 * `graphemes` MUST be referentially stable (memoize it): the key is derived from its values, but
 * the page maps are re-derived on identity change.
 */
export function usePagedGraphemeTileData({
  enabled = true,
  graphemes,
  pageSize = GRAPHEME_TILE_DATA_PAGE_SIZE,
  requiredCount,
}: {
  enabled?: boolean;
  graphemes: readonly string[];
  pageSize?: number;
  /** How many leading graphemes are revealed — pages are fetched until this many have data. */
  requiredCount: number;
}): PagedGraphemeTileData {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const readMorphemeStagesPage = useReadMorphemeStagesPage();
  const withStageVersion = useMorphemeStagesTableChangeVersion();
  const graphemeCount = arrayLength(graphemes);

  const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isFetchNextPageError } =
    useInfiniteQuery(
      infiniteQueryOptions({
        enabled: enabled && graphemeCount > 0,
        queryFn: async ({ client, pageParam }): Promise<GraphemeTileDataPage> => {
          const pageGraphemes = uniq(graphemes.slice(pageParam, pageParam + pageSize)).sort();
          const [stageByGrapheme, pinyinByGrapheme] = await Promise.all([
            readMorphemeStagesPage(client, pageGraphemes),
            client.fetchQuery(graphemePinyinLookupQueryOptions(contentDb, pageGraphemes)),
          ]);
          return {
            offset: pageParam,
            sliceByGrapheme: buildGraphemeMorphemeSliceByGrapheme(pageGraphemes, pinyinByGrapheme),
            stageByGrapheme,
          };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage: GraphemeTileDataPage): number | undefined => {
          const nextOffset = lastPage.offset + pageSize;
          return nextOffset >= graphemeCount ? undefined : nextOffset;
        },
        // Keyed on the ordered list's VALUES so identity churn never restarts the sequence; the page
        // reader is a per-render closure over DB-scoped key getters and hashes to `{}`, listed only
        // to keep the dependency explicit (mirrors `useDeckCardDetailsInfiniteQuery`).
        queryKey: withStageVersion([
          "pagedGraphemeTileData",
          {
            contentDatabaseId: getTileContentDatabaseId(contentDb),
            pageSize,
            readMorphemeStagesPage,
          },
          ...graphemes,
        ] as const),
      }),
    );

  const loadedCount = useMemo(() => {
    if (data === undefined) {
      return 0;
    }
    let count = 0;
    for (const page of data.pages) {
      count = Math.max(count, Math.min(page.offset + pageSize, graphemeCount));
    }
    return count;
  }, [data, graphemeCount, pageSize]);

  // Follow the revealed window: one page at a time, never past a failed page (a retry loop would
  // otherwise hammer the database while the tiles already paint with placeholders).
  useEffect(() => {
    if (
      enabled &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isError &&
      !isFetchNextPageError &&
      loadedCount < requiredCount
    ) {
      void fetchNextPage();
    }
  }, [
    enabled,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchNextPageError,
    isFetchingNextPage,
    loadedCount,
    requiredCount,
  ]);

  // Placeholder slices are cached per grapheme so a tile whose page is still pending receives the
  // SAME object on every render — the tile is memoized, and a fresh placeholder each time would
  // re-render every pending tile whenever the grid's parent renders (e.g. on each reveal).
  const getPlaceholderSlice = useMemo(
    () =>
      createTileSliceCache((grapheme): MorphemeGlyphTileGraphemeMorphemeSlice => ({
        additionalPinyin: [],
        grapheme,
        morphemeType: "grapheme",
        primaryPinyin: null,
      })),
    [],
  );

  return useMemo<PagedGraphemeTileData>(() => {
    const pages = data?.pages ?? [];
    return {
      getSlice: (grapheme) => {
        for (const page of pages) {
          const slice = page.sliceByGrapheme.get(grapheme);
          if (slice !== undefined) {
            return slice;
          }
        }
        return getPlaceholderSlice(grapheme);
      },
      getStage: (grapheme) => {
        for (const page of pages) {
          const stage = page.stageByGrapheme[grapheme];
          if (stage !== undefined) {
            return stage;
          }
        }
        return PLACEHOLDER_STAGE;
      },
      loadedCount,
    };
  }, [data, getPlaceholderSlice, loadedCount]);
}
